import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { DialogContent, List, RadioGroup } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForwardSharp';
import { DateTime } from 'luxon';
import { User, DateField } from '~/modules/common/components';
import { toRepliconDate } from '~/modules/common/dates/convert';
import { ALLOCATION_POSITION } from '../hooks';
import ChangeAllocationUserOptionItem from './ChangeAllocationUserOptionItem';
import useStyles, { useDateStyles, useUserStyles } from './useStyles';
import ChangeAllocationImpactedTasksConfirmation from './ChangeAllocationImpactedTasksConfirmation';
import { ChangeAllocationUserOptionItemValue } from './constants';

const ChangeAllocationUserDialogContent = ({
  handleOptionSelect,
  handleDateChange,
  existingResourceUser,
  newResourceUser,
  selectedOption,
  changeDate,
  allocationStart,
  allocationEnd,
  permissions,
  tasksChecked,
  setTasksChecked,
  impactedTasks,
  allocationPosition
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const dateClasses = useDateStyles();
  const userClasses = useUserStyles();

  const taskConfirmation = (
    <ChangeAllocationImpactedTasksConfirmation
      selectedOption={selectedOption}
      tasksChecked={tasksChecked}
      impactedTasks={impactedTasks}
      setTasksChecked={setTasksChecked}
      existingResourceUser={existingResourceUser}
      newResourceUser={newResourceUser}
      permissions={permissions}
    />
  );
  const shouldDisableDate = useCallback(
    date =>
      DateTime.utc(date.year, date.month, date.day, 0, 0, 0, 0).equals(
        allocationStart
      ),
    [allocationStart]
  );

  return (
    <DialogContent className={classes.dialogContent}>
      <div className={classes.userChangeMessage}>
        {intl.formatMessage({
          id: 'changeAllocationUserAsOfDateDialog.changing'
        })}
        : <User user={existingResourceUser} classes={userClasses} />{' '}
        <ArrowForwardIcon
          fontSize="small"
          className={classes.pathIcon}
          mirror="rtl"
        />{' '}
        <User user={newResourceUser} classes={userClasses} />
      </div>
      {ALLOCATION_POSITION.CURRENT === allocationPosition ? (
        <RadioGroup
          aria-label={intl.formatMessage({
            id: 'changeAllocationUserAsOfDateDialog.changeResourceOptions'
          })}
          name="changeResourceOptions"
          value={selectedOption || ''}
        >
          <List component="div" className={classes.optionList} dense={false}>
            {!allocationStart.equals(allocationEnd) && (
              <ChangeAllocationUserOptionItem
                value={ChangeAllocationUserOptionItemValue.ASOFDATE}
                primary={
                  <>
                    {intl.formatMessage({
                      id: 'changeAllocationUserAsOfDateDialog.changeAsOf'
                    })}
                    <span className={classes.clickProtectionWrapper}>
                      <DateField
                        editable
                        value={toRepliconDate(changeDate)}
                        onChange={handleDateChange}
                        fullWidth={false}
                        allowKeyboardControl={false}
                        minDate={allocationStart}
                        shouldDisableDate={shouldDisableDate}
                        maxDate={allocationEnd}
                        variant="standard"
                        classes={dateClasses}
                      />
                    </span>
                  </>
                }
                onSelect={handleOptionSelect}
              />
            )}

            {selectedOption === ChangeAllocationUserOptionItemValue.ASOFDATE
              ? taskConfirmation
              : null}
            <ChangeAllocationUserOptionItem
              value={ChangeAllocationUserOptionItemValue.FULL}
              primary={intl.formatMessage({
                id:
                  'changeAllocationUserAsOfDateDialog.changeForEntireAllocation'
              })}
              onSelect={handleOptionSelect}
            />
            {selectedOption === ChangeAllocationUserOptionItemValue.FULL
              ? taskConfirmation
              : null}
          </List>
        </RadioGroup>
      ) : null}
    </DialogContent>
  );
};

ChangeAllocationUserDialogContent.propTypes = {
  handleOptionSelect: PropTypes.func,
  handleDateChange: PropTypes.func,
  existingResourceUser: PropTypes.object,
  newResourceUser: PropTypes.object,
  selectedOption: PropTypes.string,
  changeDate: PropTypes.object,
  allocationStart: PropTypes.object,
  allocationEnd: PropTypes.object,
  allocationPosition: PropTypes.oneOf([
    ALLOCATION_POSITION.CURRENT,
    ALLOCATION_POSITION.FUTURE,
    ALLOCATION_POSITION.PAST
  ]),
  permissions: PropTypes.array,
  tasksChecked: PropTypes.bool,
  setTasksChecked: PropTypes.func.isRequired,
  impactedTasks: PropTypes.array.isRequired
};

export default ChangeAllocationUserDialogContent;
